import React from 'react'
import {
  useDeleteDatasourceConfiguration,
  useGetDatasourceConfigurables,
  useGetDefaultDatasourceConfiguration,
  useUpdateOrCreateDatasourceConfiguration,
} from 'src/api/queries/microservices/platform-markets-stonex-config'
import ConfigurationPanel from 'src/components/configuration-panel/ConfigurationPanel'
import { Box, Button, Dialog, Placeholders, Text, useDisclosure } from 'src/ui'
import { useToast } from 'src/utils/toast'
import StoneXWebhookConfigurationDialog from './stonex-webhook-configuration-dialog'
import NewStoneXIntegrationGuide from './new-stonex-integration-guide'
import { hasPermission } from 'src/utils/permissions'

type StoneXConfigPanelProps = {
  data: KeyValuePair[]
  isExistingConfig: boolean
  onClose: () => void
}

export default function StoneXConfigPanel({
  data,
  isExistingConfig,
  onClose,
}: Readonly<StoneXConfigPanelProps>) {
  const toast = useToast()
  const {
    data: configurables,
    isLoading: isConfigurablesLoading,
    isError: isConfigurablesError,
  } = useGetDatasourceConfigurables()
  const {
    data: defaultConfigurations,
    isLoading: isDefaultConfigurationsLoading,
    isError: isDefaultConfigurationsError,
  } = useGetDefaultDatasourceConfiguration(!isExistingConfig)

  const { mutateAsync: updateOrCreateDatasourceConfiguration } =
    useUpdateOrCreateDatasourceConfiguration()
  const { mutateAsync: deleteDatasourceConfiguration, isPending: isDeletePending } =
    useDeleteDatasourceConfiguration()

  const deleteDisclosure = useDisclosure()
  const webhookDisclosure = useDisclosure()
  const [newConfigStep, setNewConfigStep] = React.useState<'webhook' | 'datasource'>('webhook')

  const isWebhookSetupStep = !isExistingConfig && newConfigStep === 'webhook'
  const isLoading = isConfigurablesLoading || isDefaultConfigurationsLoading
  const isError = isConfigurablesError || isDefaultConfigurationsError

  const handleWebhookClose = () => {
    webhookDisclosure.onClose()
  }

  const onSave = async (configurations: KeyValuePair[]) => {
    await updateOrCreateDatasourceConfiguration(configurations)
    toast({ title: 'Success', description: 'Updated StoneX configuration' })
    // ConfigurationPanel throws a toast on error, so we don't need to handle it here
  }

  const handleDelete = async () => {
    try {
      await deleteDatasourceConfiguration()
      deleteDisclosure.onClose()
      onClose()
      toast({ title: 'Success', description: 'Deleted StoneX configuration' })
    } catch {
      toast({ status: 'error', title: 'Error', description: 'Failed to delete configuration' })
    }
  }

  return (
    <>
      {isWebhookSetupStep ? (
        <NewStoneXIntegrationGuide
          onCancel={onClose}
          onShowWebhookConfiguration={webhookDisclosure.onOpen}
          onNext={() => setNewConfigStep('datasource')}
        />
      ) : isLoading ? (
        <Placeholders.LoadingState mt={8} />
      ) : isError ? (
        <Placeholders.FailedState mt={8} />
      ) : (
        <ConfigurationPanel
          title="StoneX"
          configurations={configurables ?? []}
          data={isExistingConfig ? data : (defaultConfigurations ?? [])}
          onSave={onSave}
          onEditCancel={() => {
            if (!isExistingConfig) {
              onClose()
            }
          }}
          openInEdit={!isExistingConfig}
          editPermission="company_properties_edit"
          alwaysExpanded
          encryptedKeys={['impersonationKey', 'serviceKey']}
          secondaryControls={
            isExistingConfig ? (
              <Box>
                {hasPermission('company_properties_edit') && (
                  <Button
                    mr={2}
                    size="sm"
                    colorScheme="secondary"
                    onClick={(e) => {
                      e.stopPropagation()
                      deleteDisclosure.onOpen()
                    }}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  size="sm"
                  colorScheme="secondary"
                  onClick={(e) => {
                    e.stopPropagation()
                    webhookDisclosure.onOpen()
                  }}
                >
                  Show Webhook Configuration
                </Button>
              </Box>
            ) : null
          }
        />
      )}

      <Dialog
        isOpen={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        title="Delete StoneX configuration"
        actions={
          <>
            <Button
              variant="ghost"
              mr={3}
              onClick={deleteDisclosure.onClose}
              isDisabled={isDeletePending}
            >
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDelete}
              isLoading={isDeletePending}
              isDisabled={isDeletePending}
            >
              Delete
            </Button>
          </>
        }
      >
        <Text>Are you sure you want to delete the StoneX configuration?</Text>
      </Dialog>

      <StoneXWebhookConfigurationDialog
        isOpen={webhookDisclosure.isOpen}
        onClose={handleWebhookClose}
      />
    </>
  )
}
